import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Eye, EyeOff, CheckCircle2, AlertTriangle, Mail } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";

const API = window.location.origin;

/**
 * Pagina pubblica di accettazione invito utente (non-Gmail).
 *
 * Flusso:
 *   1. Componente leggi `?token=…` dall'URL.
 *   2. GET /api/auth/invite/validate?token=… → email/nome/kind. Se 410
 *      mostriamo un blocco "invito scaduto" con bottone "vai al login".
 *   3. Form set-password con conferma. POST /api/auth/invite/accept.
 *   4. Successo → redirect a /login con toast.
 *
 * Per email Gmail il backend NON emette token: l'utente arriva qui solo
 * se accidentalmente apre `/accept-invite` senza un invito valido. In
 * quel caso vedrà comunque il blocco "invito scaduto/non valido".
 */
export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [info, setInfo] = useState(null); // { email, name, kind }
  const [validating, setValidating] = useState(true);
  const [validateError, setValidateError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValidateError("Link non valido: token mancante.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/invite/validate`, {
          params: { token },
        });
        if (cancelled) return;
        setInfo(data);
      } catch (err) {
        if (cancelled) return;
        setValidateError(err.response?.data?.detail || "Invito non valido o scaduto.");
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Lato client validiamo MENO del backend (UX): mostriamo subito
    // l'errore di confronto e poi rimandiamo al server per la policy.
    if (password.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    if (password !== confirm) { toast.error("Le password non corrispondono"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/invite/accept`, { token, password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'attivazione dell'account");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Stati di rendering ─────────────────────────────────────────

  if (validating) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
        <div className="text-center" data-testid="invite-loading">
          <Loader2 className="w-10 h-10 text-[#3B42DD] mx-auto mb-4 animate-spin" />
          <p className="text-slate-500 text-sm">Verifica invito in corso…</p>
        </div>
      </div>
    );
  }

  if (validateError) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
        <Toaster position="top-right" theme="light" />
        <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-md text-center" data-testid="invite-error">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Invito non valido</h2>
          <p className="text-slate-500 text-sm mb-1">{validateError}</p>
          <p className="text-slate-400 text-xs mb-6">
            Se hai già attivato il tuo account, accedi normalmente. Altrimenti chiedi all&apos;amministratore un nuovo invito.
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl"
            data-testid="invite-error-login-btn"
          >
            Vai al login
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
        <Toaster position="top-right" theme="light" />
        <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-md text-center" data-testid="invite-done">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Account attivato!</h2>
          <p className="text-slate-500 text-sm mb-6">
            Ora puoi accedere con la tua nuova password.
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl"
            data-testid="invite-done-login-btn"
          >
            Vai al login
          </Button>
        </div>
      </div>
    );
  }

  // ── Form set-password ──────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
      <Toaster position="top-right" theme="light" />
      <div className="w-full max-w-md">
        <div className="bg-[#3B42DD] rounded-3xl px-8 py-10 pb-16 relative">
          <h1 className="text-2xl font-semibold text-white mb-1">Attiva il tuo account</h1>
          <p className="text-white/70 text-sm">Imposta la tua password per accedere</p>
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#3B42DD]" />
        </div>
        <div className="bg-white rounded-3xl p-8 shadow-md -mt-8 relative z-10">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 mb-5 flex items-center gap-3" data-testid="invite-info">
            <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Account</div>
              <div className="text-sm font-semibold text-slate-800 truncate">{info?.name || info?.email}</div>
              {info?.name && (
                <div className="text-xs text-slate-500 truncate">{info.email}</div>
              )}
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Nuova Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Almeno 8 caratteri, maiuscola, minuscola, numero"
                  className="pl-10 pr-10 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl"
                  data-testid="invite-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  data-testid="invite-toggle-show-pwd"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Conferma Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ripeti la password"
                  className="pl-10 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl"
                  data-testid="invite-confirm-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl font-semibold"
              data-testid="invite-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Attiva account
            </Button>
          </form>
          <p className="text-[11px] text-slate-400 text-center mt-4">
            Link valido per 24 ore · uso singolo
          </p>
        </div>
      </div>
    </div>
  );
}
